/**
 * PatientHeaderCard — Ficha superior siempre visible con tabs horizontales
 * integradas. Se muestra en todas las pestañas del detalle del paciente para
 * mantener el contexto (foto, datos, próxima cita, alergias, acciones) mientras
 * el usuario navega por las pestañas.
 */
import {
  Phone,
  Mail,
  MapPin,
  Camera,
  Calendar,
  Building2,
  Droplet,
  AlertCircle,
  Play,
  Pill,
  Award,
  Send,
  ArrowLeft,
} from "@/components/icons";
import { Link } from "react-router-dom";
import { calcAge, fmtDate, fmtTime } from "./common";

const GENDER_LABEL = { M: "Masculino", F: "Femenino", O: "Otro" };

export default function PatientHeaderCard({
  patient,
  nextAppt,
  isDoctor,
  fileRef,
  backTo,
  tabs,
  activeTab,
  onChangeTab,
  onStartConsult,
  startingConsult,
  onCreateRx,
  onCreateCert,
  onCreateReferral,
}) {
  const allergies = (patient?.allergies || "")
    .split(/[,;·]/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div
      className="bg-[#F5F5F7] rounded-[24px] p-5"
      data-testid="patient-header-card"
    >
      {/* Row 1: Tabs horizontales + volver */}
      <div
        className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-3 border-b border-black/[0.06]"
        data-testid="patient-tabs"
      >
        <Link
          to={backTo}
          data-testid="patient-back"
          title="Volver"
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-black/60 hover:text-black hover:bg-white/70 transition flex-shrink-0"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} weight="regular" />
        </Link>
        {tabs.map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onChangeTab(t.id)}
              data-testid={`patient-tab-${t.id}`}
              className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-semibold transition whitespace-nowrap flex-shrink-0 ${
                active
                  ? "bg-white text-black"
                  : "bg-white/50 text-black/55 hover:text-black hover:bg-white"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  active ? "bg-[#22C55E]" : "bg-black/20"
                }`}
              />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Row 2: Foto + datos personales + próxima cita */}
      <div className="mt-4 flex items-start gap-5">
        <button
          type="button"
          onClick={() => fileRef?.current?.click()}
          className="relative w-20 h-20 rounded-full overflow-hidden bg-white flex-shrink-0 group"
          title="Cambiar foto"
          data-testid="patient-photo"
        >
          {patient.photo_url ? (
            <img
              src={patient.photo_url}
              alt={patient.full_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-black text-[22px] font-bold">
              {(patient.full_name || "?")[0]?.toUpperCase()}
            </div>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 flex items-center justify-center transition">
            <Camera
              className="w-4 h-4 text-white opacity-0 group-hover:opacity-100"
              strokeWidth={1.75}
              weight="regular"
            />
          </div>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[22px] font-semibold text-black leading-tight truncate">
                {patient.full_name || "Paciente"}
              </div>
              <div className="text-[13px] text-black/55 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                <span>{calcAge(patient.birth_date)}</span>
                {patient.gender && (
                  <>
                    <span className="text-black/20">·</span>
                    <span>{GENDER_LABEL[patient.gender] || patient.gender}</span>
                  </>
                )}
                {patient.blood_type && (
                  <>
                    <span className="text-black/20">·</span>
                    <span className="inline-flex items-center gap-1">
                      <Droplet
                        className="w-3 h-3 text-[#EF4444]"
                        strokeWidth={1.75}
                        weight="fill"
                      />
                      {patient.blood_type}
                    </span>
                  </>
                )}
                {patient.document_id && (
                  <>
                    <span className="text-black/20">·</span>
                    <span>{patient.document_id}</span>
                  </>
                )}
              </div>
            </div>

            {nextAppt && (
              <div className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-[#0071E3]/10 text-[#0071E3] text-[11px] font-semibold whitespace-nowrap">
                <Calendar
                  className="w-3 h-3"
                  strokeWidth={1.75}
                  weight="regular"
                />
                Próxima: {fmtDate(nextAppt.date)} · {fmtTime(nextAppt.date)}
              </div>
            )}
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-[12.5px]">
            <ContactItem
              icon={Phone}
              value={patient.phone}
              href={patient.phone ? `tel:${patient.phone}` : null}
            />
            <ContactItem
              icon={Mail}
              value={patient.email}
              href={patient.email ? `mailto:${patient.email}` : null}
            />
            <ContactItem icon={MapPin} value={patient.address} />
            <ContactItem icon={Building2} value={patient.office_name} />
          </div>
        </div>
      </div>

      {/* Row 3: Alergias banner (si hay) */}
      {allergies.length > 0 && (
        <div
          className="mt-4 flex items-start gap-2.5 rounded-xl bg-[#FEF3F2] border border-[#FEE4E2] px-3.5 py-2.5"
          data-testid="patient-allergy-banner"
        >
          <AlertCircle
            className="w-4 h-4 text-[#B42318] flex-shrink-0 mt-0.5"
            strokeWidth={1.75}
            weight="regular"
          />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#B42318]">
              Alergias
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {allergies.map((a, i) => (
                <span
                  key={i}
                  className="inline-flex h-6 items-center px-2 rounded-md bg-white text-[12px] font-semibold text-[#B42318] border border-[#FDA29B]"
                >
                  {a}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Row 4: Acciones del doctor */}
      {isDoctor && (
        <div
          className="mt-4 pt-4 border-t border-black/[0.06] flex flex-wrap items-center justify-end gap-1.5"
          data-testid="patient-actions-bar"
        >
          <button
            onClick={onStartConsult}
            disabled={startingConsult}
            data-testid="start-consult-btn"
            className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full bg-[#0071E3] text-white hover:bg-[#0077ED] text-[12px] font-semibold transition disabled:opacity-60"
          >
            <Play className="w-3 h-3" strokeWidth={1.75} weight="regular" />
            {startingConsult ? "Iniciando…" : "Iniciar consulta"}
          </button>
          <button
            onClick={onCreateRx}
            data-testid="create-rx-btn"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white hover:bg-black/[0.03] text-[12px] font-semibold text-black transition"
          >
            <Pill className="w-3 h-3" strokeWidth={1.75} weight="regular" />
            Crear receta
          </button>
          <button
            onClick={onCreateCert}
            data-testid="create-cert-btn"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white hover:bg-black/[0.03] text-[12px] font-semibold text-black transition"
          >
            <Award className="w-3 h-3" strokeWidth={1.75} weight="regular" />
            Crear certificado
          </button>
          <button
            onClick={onCreateReferral}
            data-testid="create-referral-btn"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white hover:bg-black/[0.03] text-[12px] font-semibold text-black transition"
          >
            <Send className="w-3 h-3" strokeWidth={1.75} weight="regular" />
            Crear referido
          </button>
        </div>
      )}
    </div>
  );
}

function ContactItem({ icon: Icon, value, href }) {
  if (!value) {
    return (
      <div className="flex items-center gap-2 text-black/35 min-w-0">
        <Icon
          className="w-3.5 h-3.5 flex-shrink-0"
          strokeWidth={1.75}
          weight="regular"
        />
        <span className="text-[12.5px]">—</span>
      </div>
    );
  }
  const content = (
    <div className="flex items-center gap-2 min-w-0 text-black/70">
      <Icon
        className="w-3.5 h-3.5 flex-shrink-0 text-black/40"
        strokeWidth={1.75}
        weight="regular"
      />
      <span className="truncate">{value}</span>
    </div>
  );
  return href ? (
    <a href={href} className="hover:text-[#0071E3] transition-colors">
      {content}
    </a>
  ) : (
    content
  );
}
